/**
 * The world, as the player sees it.
 *
 * A Three.js night city (see world/renderer.ts) replaces the old MapLibre
 * map. This component owns everything around the canvas: streaming chunks for
 * whatever the camera can see, deciding which labels are worth pinning, the
 * overlay controls, and the search bar that flies the camera anywhere.
 */

import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import type { WorldTileResponse } from "@parcelbound/contracts";
import { api } from "./api";
import { EyeIcon, FootTrafficIcon, PickaxeIcon, TruckIcon } from "./icons";
import { useApp, type ChunkState, type MapLayer } from "./store";
import { FETCH_DIST, MAP_VIEW_DIST, WorldRenderer, type LabelSpec } from "./world/renderer";
import { ringCentre } from "./world/geometry";

const MAX_LABELS = 26;

/** Centre of a chunk, from its XYZ id. Plain tile arithmetic. */
function chunkCentre(chunkId: string): [number, number] | null {
  const m = /^(\d+)\/(\d+)\/(\d+)$/.exec(chunkId);
  if (!m) return null;
  const span = 2 ** Number(m[1]);
  const lon = ((Number(m[2]) + 0.5) / span) * 360 - 180;
  const n = Math.PI * (1 - (2 * (Number(m[3]) + 0.5)) / span);
  return [lon, (180 / Math.PI) * Math.atan(Math.sinh(n))];
}

export function WorldView(): JSX.Element {
  const holder = useRef<HTMLDivElement>(null);
  const labelLayer = useRef<HTMLDivElement>(null);
  const fpsNode = useRef<HTMLDivElement>(null);
  const world = useRef<WorldRenderer | null>(null);
  const inFlight = useRef(false);

  const chunks = useApp((s) => s.chunks);
  const selectedId = useApp((s) => s.selectedId);
  const floorPlan = useApp((s) => s.floorPlan);
  const selectedRoomId = useApp((s) => s.selectedRoomId);
  const mapLayer = useApp((s) => s.mapLayer);
  const showResources = useApp((s) => s.showResources);
  const [idleTick, setIdleTick] = useState(0);

  // Create the renderer once, opening on the player's own neighbourhood.
  useEffect(() => {
    if (!holder.current || !labelLayer.current || world.current) return;
    const home = useApp.getState().session?.homeChunkId;
    const centre = (home ? chunkCentre(home) : null) ?? [-74.0, 40.7358];
    const w = new WorldRenderer({
      container: holder.current,
      labelLayer: labelLayer.current,
      fpsNode: fpsNode.current,
      home: centre,
      onPickParcel: (id) => useApp.getState().select(id),
      onPickRoom: (id) => {
        const { selectedRoomId: current, selectRoom } = useApp.getState();
        selectRoom(current === id ? null : id);
      },
      onIdle: () => setIdleTick((n) => n + 1),
    });
    world.current = w;
    // Always exposed: the Businesses window reads the camera position when
    // placing a mobile cart, and the dev console gets a handle for free.
    (window as unknown as { __pbWorld?: WorldRenderer }).__pbWorld = w;
    return () => {
      // Always tear down. React's development double-invoke then builds a
      // fresh renderer; skipping disposal here used to leave the first one
      // alive, so two renderers drew into the same container and each pinned
      // its own copy of every label.
      w.dispose();
      if (world.current === w) world.current = null;
    };
  }, []);

  // The camera settled somewhere: make sure that somewhere exists.
  useEffect(() => {
    const w = world.current;
    if (!w) return;
    void refresh(w);
  }, [idleTick]);

  // Dock avatar clicked: recentre the camera on the in-world character.
  const goToPlayerTick = useApp((s) => s.goToPlayerTick);
  useEffect(() => {
    if (goToPlayerTick === 0) return;
    world.current?.focusPlayer();
  }, [goToPlayerTick]);

  // Far zoom: feed the world map its overview layers — generated-area roads
  // and the player's businesses as clustered, clickable markers.
  const overviewAt = useRef(0);
  useEffect(() => {
    const w = world.current;
    if (!w || w.targetDistance() <= MAP_VIEW_DIST) return;
    if (Date.now() - overviewAt.current < 60_000) return;
    overviewAt.current = Date.now();

    api.worldOverview().then(
      (r) => world.current?.setOverviewRoads(r.roads.map((road) => road.line)),
      () => undefined,
    );
    api.facilities().then(
      (r) => {
        const byChunk = new Map<string, { lngLat: [number, number]; count: number }>();
        for (const f of r.facilities) {
          if (!f.centroid) continue;
          const key = `${Math.round(f.centroid[0] * 50)}:${Math.round(f.centroid[1] * 50)}`;
          const entry = byChunk.get(key);
          if (entry) entry.count += 1;
          else byChunk.set(key, { lngLat: f.centroid, count: 1 });
        }
        world.current?.setMapMarkers(
          [...byChunk.values()].map((c) => ({
            lngLat: c.lngLat,
            el: mapMarker(c.count, () => world.current?.flyTo(c.lngLat, 1_800)),
          })),
        );
      },
      () => undefined,
    );
  }, [idleTick]);

  // Map view below continental range: fetch the detailed political layer
  // (10m coastlines, region borders, city labels) for what the camera sees.
  // Keyed by a rounded bbox + the server's LOD band so settling in the same
  // place never refetches.
  const adminKey = useRef("");
  useEffect(() => {
    const w = world.current;
    if (!w || !w.isMapView()) return;
    const dist = w.targetDistance();
    if (dist >= 3_000_000) return;
    const b = w.mapViewBounds();
    const band = dist >= 2_500_000 ? "low" : dist >= 400_000 ? "mid" : "full";
    const key = `${band}:${b.west.toFixed(1)}:${b.south.toFixed(1)}:${b.east.toFixed(1)}:${b.north.toFixed(1)}`;
    if (key === adminKey.current) return;
    adminKey.current = key;

    api.adminAreas(b, dist).then(
      (r) => world.current?.setAdminAreas(r),
      () => {
        adminKey.current = ""; // retry on the next idle
      },
    );
    api.mapLabels(b, dist).then(
      (r) => world.current?.setMapLabels(r.labels),
      () => undefined,
    );
  }, [idleTick]);

  // Push world data and selection state into the scene.
  useEffect(() => {
    const w = world.current;
    if (!w) return;
    w.setChunks(chunks, {
      myId: useApp.getState().session?.id ?? null,
      selectedId,
      mapLayer,
      showResources,
      cutawayBuildingId: floorPlan?.buildingId ?? null,
      cutawayBaseM: floorPlan
        ? (floorPlan.heightM / Math.max(1, floorPlan.floors)) * floorPlan.floor
        : 0,
    });
  }, [chunks, selectedId, mapLayer, showResources, floorPlan]);

  useEffect(() => {
    world.current?.setFloorPlan(floorPlan, selectedRoomId, useApp.getState().session?.id ?? null);
  }, [floorPlan, selectedRoomId]);

  // Logistics lanes while the overlay is on, with couriers running them.
  useEffect(() => {
    const w = world.current;
    if (!w) return;
    if (mapLayer !== "logistics") {
      w.setLanes([]);
      return;
    }
    let live = true;
    const load = (): void => {
      api.logistics().then(
        (r) => {
          if (!live || !world.current) return;
          world.current.setLanes(
            r.lanes
              .filter((l) => l.from.at && l.to.at)
              .map((l) => ({ from: l.from.at!, to: l.to.at! })),
          );
        },
        () => undefined,
      );
    };
    load();
    const reload = setInterval(load, 5_000);
    return () => {
      live = false;
      clearInterval(reload);
      world.current?.setLanes([]);
    };
  }, [mapLayer]);

  // Label pills: HTML so they style with the rest of the chrome. The renderer
  // pins them to world positions; this effect only decides what deserves one.
  useEffect(() => {
    const w = world.current;
    if (!w) return;
    const myId = useApp.getState().session?.id ?? null;
    const centre = w.cameraLngLat();

    type Candidate = LabelSpec & { rank: number };
    const candidates: Candidate[] = [];

    if (floorPlan) {
      const floorBase = (floorPlan.heightM / Math.max(1, floorPlan.floors)) * floorPlan.floor;
      for (const unit of floorPlan.units) {
        for (const room of unit.rooms) {
          // The big white $ marks what can still be bought; sold rooms lose it.
          if (!room.sellable || room.owner) continue;
          candidates.push({
            lngLat: ringCentre(room.polygon),
            altitude: floorBase + 1.6,
            el: roomPill(room, `$${trimCents(room.appraisedValue)}`),
            rank: -1,
          });
        }
      }
    }

    for (const chunk of Object.values(chunks)) {
      for (const p of chunk.parcels) {
        const mine = p.owner !== null && p.owner.id === myId;
        if (mine) {
          candidates.push({ lngLat: p.centroid, el: pill("YOURS", "mine", p.id), rank: 0 });
        } else if (p.listing) {
          candidates.push({
            lngLat: p.centroid,
            el: pill(`$${trimCents(p.listing.price)}`, "price", p.id),
            rank: 1,
          });
        } else if (p.owner) {
          candidates.push({
            lngLat: p.centroid,
            el: pill(p.owner.displayName, "other", p.id),
            rank: 2,
          });
        }
      }
      for (const b of chunk.buildings) {
        if (b.owner?.id === myId && b.unitCount > 0 && b.vacantUnitCount === b.unitCount) {
          candidates.push({
            lngLat: b.centroid,
            altitude: b.heightM + 2,
            el: pill("⚠ No tenants", "warn", b.parcelId),
            rank: 3,
          });
        }
      }
    }

    candidates.sort(
      (a, b) =>
        a.rank - b.rank ||
        Math.hypot(a.lngLat[0] - centre[0], a.lngLat[1] - centre[1]) -
          Math.hypot(b.lngLat[0] - centre[0], b.lngLat[1] - centre[1]),
    );
    w.setLabels(candidates.slice(0, MAX_LABELS));
  }, [chunks, selectedId, mapLayer, showResources, floorPlan, selectedRoomId, idleTick]);

  useEffect(() => () => world.current?.setLabels([]), []);

  /** Ask for the visible chunks, then pull features for the ready ones. */
  async function refresh(w: WorldRenderer): Promise<void> {
    if (w.targetDistance() > FETCH_DIST) return; // too far out for gameplay data
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const { chunkIds } = await api.chunksForViewport(w.viewBounds());
      if (chunkIds.length === 0) return;
      // Nearest the camera first, so the budget lands where the player looks.
      const centre = w.cameraLngLat();
      const wanted = [...chunkIds]
        .sort((x, y) => {
          const cx = chunkCentre(x);
          const cy = chunkCentre(y);
          if (!cx || !cy) return 0;
          return (
            Math.hypot(cx[0] - centre[0], cx[1] - centre[1]) -
            Math.hypot(cy[0] - centre[0], cy[1] - centre[1])
          );
        })
        .slice(0, 24);
      await api.ensureChunks(wanted);
      await pollUntilSettled(wanted);
    } catch (err) {
      useApp.getState().notify("err", err instanceof Error ? err.message : "could not load the map");
    } finally {
      inFlight.current = false;
    }
  }

  /** Watch requested chunks, pulling features as each becomes ready. */
  async function pollUntilSettled(chunkIds: string[]): Promise<void> {
    const deadline = Date.now() + 90_000;
    for (;;) {
      const status = await api.chunkStatus(chunkIds);
      const states: Record<string, ChunkState> = {};
      for (const c of status.chunks) states[c.chunkId] = c.state;
      useApp.getState().setChunkStates(states);

      const known = useApp.getState().chunks;
      const newlyReady = status.chunks.filter((c) => c.state === "ready" && !known[c.chunkId]);
      await Promise.all(
        newlyReady.map(async (c) => {
          const features: WorldTileResponse = await api.chunkFeatures(c.chunkId);
          useApp.getState().setChunk(c.chunkId, features);
        }),
      );

      const outstanding = status.chunks.filter(
        (c) => c.state === "unseen" || c.state === "queued" || c.state === "generating",
      );
      if (outstanding.length === 0) return;
      if (Date.now() > deadline) return;
      await new Promise((r) => setTimeout(r, 1200));
    }
  }

  // Recentre when a property is chosen from a list rather than the world.
  useEffect(() => {
    const w = world.current;
    if (!w || !selectedId) return;
    for (const chunk of Object.values(chunks)) {
      const p = chunk.parcels.find((x) => x.id === selectedId);
      if (!p) continue;
      const c = w.cameraLngLat();
      if (Math.hypot(c[0] - p.centroid[0], c[1] - p.centroid[1]) > 0.004) {
        w.flyTo(p.centroid);
      }
      return;
    }
  }, [selectedId, chunks]);

  return (
    <>
      <div className="world" ref={holder} />
      <div className="world-labels" ref={labelLayer} aria-hidden />
      <SearchBar getWorld={() => world.current} />
      <LayerControls getWorld={() => world.current} />
      <ResourceLegend />
      <FootTrafficLegend />
      <FloorBar />
      <div className="fps" ref={fpsNode} aria-hidden />
      <div className="world-hint" aria-hidden>
        drag: pan · wheel: zoom · right-click: walk · right-drag: rotate · click a building: inspect
      </div>
    </>
  );
}

/* ---------------- resources legend ---------------- */

/**
 * The survey bar shown while the resources overlay is up: what the two
 * colours mean and how to read the gradient. Esc closes the overlay.
 */
function ResourceLegend(): JSX.Element | null {
  const show = useApp((s) => s.showResources);
  const toggle = useApp((s) => s.toggleResources);

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT")) return;
      if (e.key === "Escape") {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, toggle]);

  if (!show) return null;
  return (
    <div className="overlay-legend" role="note">
      <span className="chip">
        <i className="swatch ore" aria-hidden />
        Iron ore
      </span>
      <span className="chip">
        <i className="swatch concrete" aria-hidden />
        Concrete
      </span>
      <span className="grade ore-grade" aria-hidden />
      <span className="grade-label">faint → rich</span>
      <span className="hint">
        Richer ground holds bigger deposits. Mines need ore, quarries need stone. Esc exits.
      </span>
    </div>
  );
}

/**
 * The help bar shown while the foot-traffic view is up. Esc drops back to the
 * plain world view.
 */
function FootTrafficLegend(): JSX.Element | null {
  const layer = useApp((s) => s.mapLayer);
  const setMapLayer = useApp((s) => s.setMapLayer);
  const show = layer === "foot_traffic";

  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT")) return;
      if (e.key === "Escape") {
        e.preventDefault();
        setMapLayer("world");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, setMapLayer]);

  if (!show) return null;
  return (
    <div className="overlay-legend" role="note">
      <span className="chip traffic-title">Foot traffic</span>
      <span className="grade traffic-grade" aria-hidden />
      <span className="hint">
        <b className="busy">Green</b> = packed with customers · <b className="quiet">red</b> = quiet.
        Streets glow where people walk. Esc exits.
      </span>
    </div>
  );
}

/* ---------------- interior floor bar ---------------- */

/**
 * The reference's cutaway toolbar: which building and floor the interior view
 * is showing, ↑/↓ to change floor, esc to step back out to the street.
 */
function FloorBar(): JSX.Element | null {
  const plan = useApp((s) => s.floorPlan);
  const floor = useApp((s) => s.planFloor);
  const setFloor = useApp((s) => s.setPlanFloor);

  useEffect(() => {
    if (!plan) return;
    const onKey = (e: KeyboardEvent): void => {
      // Never steal keys from a form field or the search bar.
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT")) return;
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setFloor(Math.min(plan.floors - 1, floor + 1));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setFloor(Math.max(0, floor - 1));
      } else if (e.key === "Escape") {
        useApp.getState().select(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [plan, floor, setFloor]);

  if (!plan) return null;
  return (
    <>
      <div className="floor-bar" role="toolbar" aria-label="Interior floor">
        <span className="name">{plan.label} ▾</span>
        <button
          className="stepper"
          disabled={floor >= plan.floors - 1}
          onClick={() => setFloor(Math.min(plan.floors - 1, floor + 1))}
          title="Up one floor (↑)"
        >
          Floor {floor + 1} of {plan.floors} <span className="arr">▲</span>
        </button>
        <span className="sep">⇅ floor</span>
        <span className="sep">·</span>
        <button className="quiet" onClick={() => useApp.getState().select(null)}>
          esc exit
        </button>
      </div>
      <div className="kbd-hints" aria-hidden>
        <span>
          <kbd>Esc</kbd> step out
        </span>
        <span>
          <kbd>↑</kbd>
          <kbd>↓</kbd> floor
        </span>
      </div>
    </>
  );
}

/* ---------------- search ---------------- */

/**
 * Fly-anywhere search. Understands "lat, lon" pairs and a small gazetteer of
 * cities — the world generates wherever the camera lands, so every result is
 * somewhere you can actually play.
 */
const CITIES: Array<[string, number, number]> = [
  ["new york", -74.0, 40.7358],
  ["manhattan", -74.0, 40.7358],
  ["london", -0.1276, 51.5072],
  ["paris", 2.3522, 48.8566],
  ["berlin", 13.405, 52.52],
  ["copenhagen", 12.5683, 55.6761],
  ["aarhus", 10.2107, 56.1567],
  ["holstebro", 8.6212, 56.3626],
  ["amsterdam", 4.9041, 52.3676],
  ["madrid", -3.7038, 40.4168],
  ["barcelona", 2.1734, 41.3851],
  ["rome", 12.4964, 41.9028],
  ["lisbon", -9.1393, 38.7223],
  ["vienna", 16.3738, 48.2082],
  ["prague", 14.4378, 50.0755],
  ["warsaw", 21.0122, 52.2297],
  ["stockholm", 18.0686, 59.3293],
  ["oslo", 10.7522, 59.9139],
  ["helsinki", 24.9384, 60.1699],
  ["dublin", -6.2603, 53.3498],
  ["athens", 23.7275, 37.9838],
  ["istanbul", 28.9784, 41.0082],
  ["moscow", 37.6173, 55.7558],
  ["dubai", 55.2708, 25.2048],
  ["mumbai", 72.8777, 19.076],
  ["delhi", 77.1025, 28.7041],
  ["singapore", 103.8198, 1.3521],
  ["hong kong", 114.1694, 22.3193],
  ["shanghai", 121.4737, 31.2304],
  ["beijing", 116.4074, 39.9042],
  ["tokyo", 139.6503, 35.6762],
  ["osaka", 135.5023, 34.6937],
  ["seoul", 126.978, 37.5665],
  ["sydney", 151.2093, -33.8688],
  ["melbourne", 144.9631, -37.8136],
  ["auckland", 174.7633, -36.8485],
  ["los angeles", -118.2437, 34.0522],
  ["san francisco", -122.4194, 37.7749],
  ["chicago", -87.6298, 41.8781],
  ["toronto", -79.3832, 43.6532],
  ["vancouver", -123.1207, 49.2827],
  ["mexico city", -99.1332, 19.4326],
  ["são paulo", -46.6333, -23.5505],
  ["sao paulo", -46.6333, -23.5505],
  ["buenos aires", -58.3816, -34.6037],
  ["rio de janeiro", -43.1729, -22.9068],
  ["cape town", 18.4241, -33.9249],
  ["johannesburg", 28.0473, -26.2041],
  ["lagos", 3.3792, 6.5244],
  ["nairobi", 36.8219, -1.2921],
  ["cairo", 31.2357, 30.0444],
];

function resolvePlace(query: string): [number, number] | null {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  // "40.7358, -74.0" (lat, lon) — the order every map app shows.
  const pair = /^(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)$/.exec(q);
  if (pair) {
    const a = Number(pair[1]);
    const b = Number(pair[2]);
    if (Math.abs(a) <= 85 && Math.abs(b) <= 180) return [b, a];
    if (Math.abs(b) <= 85 && Math.abs(a) <= 180) return [a, b];
    return null;
  }
  for (const [name, lon, lat] of CITIES) {
    if (name.startsWith(q) || q.startsWith(name)) return [lon, lat];
  }
  return null;
}

function SearchBar({ getWorld }: { getWorld: () => WorldRenderer | null }): JSX.Element {
  const [query, setQuery] = useState("");
  const [missed, setMissed] = useState(false);

  const go = (): void => {
    const at = resolvePlace(query);
    if (!at) {
      setMissed(true);
      return;
    }
    setMissed(false);
    getWorld()?.flyTo(at, 1_800);
    setQuery("");
  };

  return (
    <div className={`search-bar${missed ? " missed" : ""}`}>
      <input
        type="search"
        placeholder="Go to any location"
        aria-label="Go to any location"
        autoComplete="off"
        spellCheck={false}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setMissed(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") go();
          if (e.key === "Escape") {
            setQuery("");
            setMissed(false);
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
    </div>
  );
}

/* ---------------- overlay controls ---------------- */

const LAYERS: Array<{
  id: MapLayer;
  label: string;
  Icon: (p: { className?: string }) => JSX.Element;
}> = [
  { id: "world", label: "World", Icon: EyeIcon },
  { id: "foot_traffic", label: "Foot traffic", Icon: FootTrafficIcon },
  { id: "logistics", label: "Logistics", Icon: TruckIcon },
];

function LayerControls({ getWorld }: { getWorld: () => WorldRenderer | null }): JSX.Element {
  const mapLayer = useApp((s) => s.mapLayer);
  const setMapLayer = useApp((s) => s.setMapLayer);
  const showResources = useApp((s) => s.showResources);
  const toggleResources = useApp((s) => s.toggleResources);

  return (
    <>
      <div className="map-panel layers" role="group" aria-label="Map overlay">
        {LAYERS.map(({ id, label, Icon }) => (
          <button
            key={id}
            className="layer-btn"
            aria-pressed={mapLayer === id && !showResources}
            onClick={() => setMapLayer(id)}
          >
            <Icon />
            {label}
          </button>
        ))}
      </div>

      <div className="map-panel resources">
        <button
          className="layer-btn"
          aria-pressed={showResources}
          onClick={toggleResources}
          title="Shade parcels by extraction potential"
        >
          <PickaxeIcon />
          Resources
        </button>
      </div>

      <div className="map-zoom">
        <button title="Zoom in" onClick={() => getWorld()?.zoomBy(1.45)}>
          +
        </button>
        <button title="Zoom out" onClick={() => getWorld()?.zoomBy(1 / 1.45)}>
          −
        </button>
        <button title="Toggle 3D tilt" onClick={() => getWorld()?.toggleTilt()}>
          3D
        </button>
        <button title="Reset bearing" onClick={() => getWorld()?.resetBearing()}>
          ⌖
        </button>
      </div>
    </>
  );
}

/* ---------------- label elements ---------------- */

function pill(text: string, kind: string, parcelId: string): HTMLElement {
  const el = document.createElement("button");
  el.className = `map-pill ${kind}`;
  el.type = "button";
  // Text node, never innerHTML: player display names are untrusted input.
  el.textContent = text;
  el.addEventListener("click", (e) => {
    e.stopPropagation();
    useApp.getState().select(parcelId);
  });
  return el;
}

/** A clustered business marker on the world map: storefront glyph + count. */
function mapMarker(count: number, onClick: () => void): HTMLElement {
  const el = document.createElement("button");
  el.className = "map-marker";
  el.type = "button";
  el.title = `${count} business${count === 1 ? "" : "es"} here — click to fly in`;
  const glyph = document.createElement("span");
  glyph.className = "glyph";
  glyph.textContent = "🏪";
  el.appendChild(glyph);
  if (count > 1) {
    const badge = document.createElement("span");
    badge.className = "count";
    badge.textContent = String(count);
    el.appendChild(badge);
  }
  el.addEventListener("click", (e) => {
    e.stopPropagation();
    onClick();
  });
  return el;
}

function roomPill(
  room: { id: string; label: string; floor: number; areaM2: number },
  price: string,
): HTMLElement {
  const el = document.createElement("button");
  el.className = "room-dollar";
  el.type = "button";

  const glyph = document.createElement("span");
  glyph.className = "glyph";
  glyph.textContent = "$";
  el.appendChild(glyph);

  // The hover card: name, where and how big, price, availability, the verb.
  const tip = document.createElement("span");
  tip.className = "tip-card";
  const line = (cls: string, text: string): void => {
    const s = document.createElement("span");
    s.className = cls;
    s.textContent = text;
    tip.appendChild(s);
  };
  line("nm", room.label);
  line("meta", `Floor ${room.floor + 1} · ${Math.round(room.areaM2)} m² · 1 room`);
  line("px", `${price} to buy`);
  line("avail", "● Available to buy");
  line("verb", "Click to buy");
  el.appendChild(tip);

  el.addEventListener("click", (e) => {
    e.stopPropagation();
    const { selectedRoomId, selectRoom } = useApp.getState();
    selectRoom(selectedRoomId === room.id ? null : room.id);
  });
  return el;
}

/** Drop trailing cents on map labels, where space is tight. */
function trimCents(wire: string): string {
  const n = Number(wire);
  if (!Number.isFinite(n)) return wire;
  return Math.round(n).toLocaleString();
}
